import React, { useState,useEffect } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { i18nIntl, numericOperators, simpleTextOperators } from '../../lib/ep.js';

import { CreateButton, SquaredButton } from '../builder/ActionButton';
import ActionLink from '../builder/ActionLink';
import ButtonCollection from '../builder/ButtonCollection.js';
import { DeleteButton } from '../builder/ConfirmButton';
import Checkbox from '../builder/Checkbox';
import FloatingLayer from '../builder/FloatingLayer';
import Input from '../builder/Input';
import Message, { AlertMessage } from '../builder/Message';
import Model from '../builder/Model';
import Range from '../builder/Range';
import RichEditor from '../builder/RichEditor';
import SaveButton from '../builder/SaveButton';
import SectionTitle from '../builder/SectionTitle';
import Select from '../builder/Select';

import transferPlaceholder from '../../hoc/transferPlaceholder';
import DateTimePicker from '../builder/DateTimePicker.js';
import Tooltip from '../builder/Tooltip.js';
import BuilderSubSectionTitle from './BuilderSubSectionTitle.js';
import { OCRTutorialMessage } from '../../containers/stages/StageReqsSidebar.js';

const RichPlaceHolder = injectIntl(transferPlaceholder(RichEditor));

const OcrSidebar = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m,props.intl, c);

	const [ condition, setCondition ] = useState(false);
	const [ is_editing, setIsEditing ] = useState(false);
	const [ errors, setErrors ] = useState(false);

	const conditions = stage?.reqs_ocr_conditions ?? [];

	useEffect(() => {
		if(!stage.reqs_ocr_verify_amount){
			const conditions_without_amount = conditions.filter((c) => { return c.k != 'ticket_amount'});
			change('reqs_ocr_conditions', [...conditions_without_amount]);
		}
	}, [stage.reqs_ocr_verify_amount]);

	useEffect(() => {
		if(!stage.reqs_ocr_date_format){
			const conditions_without_date = conditions.filter((c) => { return c.k != 'date'});
			change('reqs_ocr_conditions', [...conditions_without_date]);
		}
	}, [stage.reqs_ocr_date_format]);

	const newCondition = () => {
		setIsEditing(false);
		setCondition({
			k:'',
			op:'',
			v:"",
		});
	};

	const addCondition = (e, cb) => {
		if(is_editing !== false) {
			conditions[is_editing] = { ...condition };
			change('reqs_ocr_conditions', [...conditions]);
		} else {
			change('reqs_ocr_conditions', [...conditions, { ...condition }]);
		}

		setCondition(false);
		if(cb != undefined) cb();
		if(props.saveStage != undefined) props.saveStage();
	};

	const resetTest = () => {
		if (props.reset_req != undefined) props.reset_req();
	};

	const changeCondition = (k, v) => {
		if(condition.k == 'date' && k == 'v' && v) v = v.format('YYYY-MM-DD');

		condition[k] = v;

		if(condition.k && condition.k == "ticket_amount") {
			if(k == 'v') {
				if(!/^[0-9]+(\.[0-9]{1,2})?$/.test(v)) {
					setErrors({ v:'wrong format' });
				} else if (errors) {
					setErrors(false);
				}
			}
		}
		setCondition({ ...condition });
	};

	const editCondition = (index) => {
		setIsEditing(index);
		setCondition(conditions[index]);
	};

	const removeCondition = (index) => {
		conditions.splice(index, 1);
		change('reqs_ocr_conditions', (conditions.length == 0) ? null : conditions.slice(0));
	};

	const parseDebugText = (text) => {
		let _text = text;
		conditions.map((cond, c) => {
			if (cond.op == 'REGEX') {
				_text = _text.replaceAll(new RegExp(`(${cond.v})`,'gi'),'<span class="fetched">$1</span>');
				return _text;
			}
			_text = _text.replace(cond.v, `<span class="fetched">${cond.v}</span>`);
		})
		return _text;
	};

	// const mapTolerance = (value, type) => {
	// 	const tolerances = [
	// 		{ db:95, display:1 },
	// 		{ db:90, display:2 },
	// 		{ db:85, display:3 },
	// 		{ db:80, display:4 },
	// 		{ db:75, display:5 },
	// 	];

	// 	return tolerances.find((obj) => { return obj[(type == 'from_db') ? 'db' : 'display'] == value });
	// };

	const prechange = (k, v) => {
		//let _v;
		switch(k) {
			// case 'reqs_ocr_tolerance_enabled':
			// 	_v = (v) ? 95 : 100;
			// 	change('reqs_ocr_tolerance', _v);
			// 	break;

			// case 'fake_reqs_ocr_tolerance':
			// 	_v = mapTolerance(v, 'to_db');
			// 	change('reqs_ocr_tolerance', _v.db);
			// 	break;

			default:
				change(k, v);
				break;
		}
	};

	let available_conditions = [{ k:'', v:"-- " + i18n("Select field") + " --" }];

	if(stage.reqs_ocr_verify_amount) {
		available_conditions.push({
			k:'ticket_amount',
			v:i18n("Receipt amount"),
			operators:[
				{ k:'', v:"-- " + i18n("Choose operator") + " --" },
				...numericOperators,
			],
		});
	}

	available_conditions.push({
		k:'ticket_text',
		v:i18n("Receipt text"),
		operators:[
			{ k:'', v:"-- " + i18n("Choose operator") + " --" },
			...simpleTextOperators
		],
	}, {
		k:'text_length',
		v:i18n("Text length"),
		operators:[
			{ k:'', v:"-- " + i18n("Choose operator") + " --" },
			{ k:'<', v:i18n("Less than") },
			{ k:'>', v:i18n("Greater than") },
		],
	});

	if(stage.reqs_ocr_date_format) {
		available_conditions.push({
			k:'date',
			v:i18n("Date"),
			operators:[
				{ k:'', v:"-- " + i18n("Choose operator") + " --" },
				{ k:'<', v:i18n("Less than") },
				{ k:'>', v:i18n("Greater than") },
			],
		});
	}


	const tolerances = [
		{ k:'100', v:i18n("Disabled") + " (" + i18n("Not recommended for most cases") + ")" },
		{ k:'80', v:i18n("Low Tolerance (minimize false positives)") },
		{ k:'50', v:i18n("High Tolerance (minimize probability of fraud)") },
	];

	const date_formats = [
		{ k:'', v:"-- " + i18n("Select a date format") + " --",date_format:false },
		{ k:'dmy', v:i18n("DD/MM/YYYY (Europe, UK, AU, etc)"), 'date_format':"DD/MM/YYYY" },
		{ k:'mdy', v:i18n("MM/DD/YYYY (US, Canada, etc)"), 'date_format':"MM/DD/YYYY" },
		{ k:'ymd', v:i18n("YYYY/MM/DD (China, Japan, etc)"), 'date_format':"YYYY/MM/DD" },
	];
	const find_date_format = date_formats.find((c) => {return stage.reqs_ocr_date_format == c.k});
	const format_date_by_conditions = find_date_format ? find_date_format.date_format : "YYYY/MM/DD";

	const operators = (condition != false && condition.k != '') ? available_conditions.find((c) => (c.k == condition.k)).operators : [];

	const combined_operators = [...numericOperators, ...simpleTextOperators];

	const has_conditions = (conditions == undefined || conditions == null || conditions.length == 0) ? false : true;

	const default_instructions_placeholder = `<p>${i18n("The photo of the receipt must meet these requirements")}:</p><ul><li>${i18n("Only one receipt appears.")}</li><li>${i18n("The whole receipt appears.")}</li><li>${i18n("All content is readable.")}</li><li>${i18n("The receipt is not wrinkled or folded.")}</li><li>${i18n("The receipt has no stains.")}</li><li>${i18n("The reverse of the receipt is not visible. Avoid backlit photos.")}</li><li>${i18n("Make sure the receipt is in focus.")}</li></ul>`;

	const link_contact = window.web_base_url + ((window.is_spanish) ? 'contacto/' : 'contact/');
	const promotion = props.promotion != undefined ? props.promotion : {};
	const limit_ocr_month = promotion.total_ocr_month != undefined ? promotion.total_ocr_month : 5000;//No es month, es total per promo!

	return (
		<div>
			<Message
				_class={{ other:'in_top in_bottom' }}
			>
				<p>{i18n("Your OCR scans limit")}: <FormattedMessage id="{total} validations" values={{total:limit_ocr_month}} /></p>
				<p>{i18n("Need more?")} <ActionLink _href={link_contact} _target="_blank">Contact sales</ActionLink></p>
			</Message>

			{(!has_conditions && false) && (
				<Message
					title="Warning"
					_class={{ other:'in_bottom' }}
				>
					<p>{i18n("Since you have not defined any condition, all the uploaded receipts will pass the OCR validation.")}</p>
				</Message>
			)}

			<article class="gui_not_in_top">
				<Model model={stage} change={prechange}>
					{(stage.reqs_type != '3') && (
						<Checkbox
							name="reqs_ocr_check_code"
						>Check that the code appears in the image</Checkbox>
					)}

					<Checkbox
						name="reqs_ocr_verify_amount"
						label_props={{
							tooltip:<Tooltip position='top'>Enable this option to prompt the user to enter the total amount of the ticket, and the system will detect if the amount is present on the ticket. When you enable the option, you will need to (1) add a title to the field where the user is asked for the amount, and (2) create a condition to indicate the minimum amount the ticket should exceed.</Tooltip>,
						}}
					>Enable conditions based on receipt amount</Checkbox>

					{(stage.reqs_ocr_verify_amount) && (
						<Input
							name="reqs_ocr_verify_amount_label"
							placeholder="Enter the amount with decimals (XX.XX)"
						>Label for the receipt amount field</Input>
					)}

					{stage?.reqs_ocr_verify_amount && stage.type == 19 && (
					<Input	
						name="reqs_ocr_amount_x_stamp"
						label_props={{
							tooltip:<Tooltip position='top'>Enter the amount corresponding to each stamp on the card. For example, if your currency is Euro and you enter 10, it means that for every 10 Euros in the total purchase receipt, a new stamp will be issued on the card. If the amount is lower than this value, the receipt will be marked as invalid. This value helps determine how much the user needs to spend to accumulate loyalty stamps.</Tooltip>,
						}}
						>
						Amount per stamp
					</Input>
					)}


					<Checkbox
						name="reqs_ocr_date_format"
						label_props={{
							tooltip:<Tooltip position='top'>Enable this option for the system to attempt automatic ticket date detection. When you enable the field, you will need to (1) select the date format and (2) create a condition to indicate the valid date range. Only numeric date formats are supported. Dates with the full or abbreviated month name are not supported.</Tooltip>,
						}}
					>Enable conditions based on receipt date</Checkbox>

					
					{(stage.reqs_ocr_date_format != undefined && stage.reqs_ocr_date_format && stage.reqs_ocr_date_format != 'false') && (
						<Select
							name="reqs_ocr_date_format"
							values={date_formats}
							label_props={{
								tooltip:<Tooltip position='top'>The system can only auto detect the receipt date in numeric format. Receipt dates with the name of the month won't be detected.</Tooltip>,
							}}
						>Select the date format</Select>
					)}
				</Model>

				{(stage.reqs_ocr_date_format != undefined && stage.reqs_ocr_date_format && stage.reqs_ocr_date_format != 'false' && false) && (
					<Message>
						<p>{i18n("The system can only auto detect the receipt date in numeric format. Receipt dates with the name of the month won't be detected.")} <ActionLink _href={`${window.zendesk_base_url}4402344157588`} _target="_blank">More information</ActionLink></p>
					</Message>
				)}

				<BuilderSubSectionTitle
					title="Conditions"
					icon='knk-filter'
					bordered={false}
					_class='gui_not_in_top_big'
					title_actions={
						<div class="gui_floating_selector_list">
							<CreateButton
								onClick={newCondition}
								icon={false}
							>Add condition</CreateButton>
						</div>
					}
				/>

				<ul class="gui_sortable_list gui_flexbox gui_flex_start gui_flex_col gui_flex_nowrap">
					{conditions.map((c, i) => {
						const operator_txt = combined_operators.find((obj) => { return obj.k == c.op; });
						const _condition = available_conditions.find((condition) => (c.k == condition.k));
						let condition_value = c.v;
						if(c.k=='date')condition_value = EPDateFormat(condition_value, format_date_by_conditions);
						if(!_condition)return null;
						return (
							<li key={`condition_${i}`} class="gui_sortable_list_item gui_flexbox gui_flex_start gui_flex_col gui_flex_nowrap gui_flex_align_center">
								<div class="item_header">
									<div class="item_title_container">
										<strong class="item_title knk-arrow-r-big">{_condition.v}</strong>
										<p class="subtitle">{i18n(operator_txt.v)} <strong class="gui_not_in_start_tiny">{condition_value}</strong></p>
									</div>

									<ButtonCollection>
										<SquaredButton
											icon='knk-edit'
											onClick={() => editCondition(i)}
											aria-label="Edit condition"
										/>

										<DeleteButton
											squared={true}
											_position='left'
											onClick={() => removeCondition(i)}
											aria-label="Delete condition"
										/>
									</ButtonCollection>
								</div>
							</li>
						)
					})}

					{(!has_conditions) && (
						<li class="gui_sortable_list_item gui_flexbox gui_flex_start gui_flex_row gui_flex_nowrap gui_flex_align_center">
							<div class="item_header">
								<div class="item_title_container">
									<span class="item_title cutTextOnOverflow knk-off">{i18n("There are no conditions at the moment")}.</span>
								</div>
							</div>
						</li>
					)}
				</ul>

				
			</article>

				{/* 
				  * Users are no longer allowed to set this param and it's fixed to 85%
			<article class="gui_not_in_top_huge">
				<BuilderSubSectionTitle
					title="More options"
					icon='knk-settings'
				/>

					
					<Model model={stage} change={prechange}>
					<Checkbox
						name="reqs_ocr_tolerance_enabled"
						value={(stage.reqs_ocr_tolerance != undefined && parseInt(stage.reqs_ocr_tolerance) < 100) ? true : false}
						label_props={{
							tooltip:<Tooltip>Users can upload different photos for the same receipt. The OCR system has a control to minimize these practices, since the system will compare the receipt of the photo with the other receipts that the user has already uploaded and validated. If the system detects that the receipt is similar to the previous ones, it will invalidate it.</Tooltip>,
						}}
					>Fraud control due to similarity of receipts</Checkbox>

					{(stage.reqs_ocr_tolerance != undefined && parseInt(stage.reqs_ocr_tolerance) < 100) && (
						<Range
							min={1}
							max={5}
							step={1}
							value={mapTolerance(parseInt(stage.reqs_ocr_tolerance), 'from_db').display}
							name="fake_reqs_ocr_tolerance"
							default={1}
							_class={{size:40}}
							label_props={{
								tooltip:<Tooltip>The higher the level of detection, the less likely a user will cheat us, but the more likely the control will generate false positives. That is to say, invalidate tickets for considering them similar to others, when they really are different tickets.</Tooltip>,
							}}
						>Fraud Detection Level</Range>
					)}

					{(stage.reqs_ocr_tolerance != undefined && parseInt(stage.reqs_ocr_tolerance) < 100) && (
						<OCRTutorialMessage in_top={false} />
					)}

					<RichPlaceHolder
						name="reqs_ocr_instructions"
						placeholder={default_instructions_placeholder}
					>Instructions</RichPlaceHolder>
				</Model> 
			</article>
			*/}

			<article class="gui_not_in_top_huge">
				<BuilderSubSectionTitle
					title="Test"
					icon='knk-developers'
					title_actions={(props.debug.text != undefined && props.debug.text != "") ? (
						<div class="gui_floating_selector_list">
							<ActionLink onClick={resetTest}>Reset test</ActionLink>
						</div>
					) : null}
				/>

				{(props.debug.text != undefined && props.debug.text != "") ? (
					<table class="design_helper_table not_awesome" cellSpacing={0} cellPadding={0} width="100%">
						<tbody>
							<tr>
								<td class="code_selector">
									<code class="is_monospaced">
										<span class="parent is_monospaced" dangerouslySetInnerHTML={{ __html:parseDebugText(props.debug.text) }} />
									</code>
								</td>
							</tr>
						</tbody>
					</table>
				) : (
					<Message
						_class={{other:'in_bottom'}}
					>
						<p>{i18n("You can use the preview form on the right to test the system with your own receipts. The results of the test will appear in this section.")}</p>
					</Message>
				)}
			</article>

			{(condition != false) && (
				<FloatingLayer backButton={() => setCondition(false)}>
					<div>
						<SectionTitle>Add condition</SectionTitle>

						<Model
							classname="gui_flexbox gui_flex_row gui_flex_nowrap gui_margined_children_low"
							errors={errors}
							model={condition}
							change={changeCondition}
						>
							<Select
								name="k"
								value={condition.k}
								values={available_conditions}
							>Field</Select>

							<Select
								name="op"
								value={condition.op}
								values={operators}
							>Operator</Select>

							{condition.k == 'date' && (
								<DateTimePicker
									name="v"
									value={condition.v != "" ?condition.v : null}
									date_format={format_date_by_conditions}
									show_time={false}
								>Date</DateTimePicker>
							)}

							{condition.k != 'date' && (
								<Input
									name="v"
								>Value</Input>
							)}
						</Model>

						<ButtonCollection in_floating={true}>
							<SaveButton
								disabled={(errors != false) ? true : false}
								onClick={addCondition}
							>{(is_editing !== false) ? "Edit condition" : "Add condition"}</SaveButton>
						</ButtonCollection>
					</div>
				</FloatingLayer>
			)}
		</div>
	);
}

export default injectIntl(OcrSidebar);